import React from 'react'
import { X, Clock, Mountain } from 'lucide-react'
import { waypointIcons } from '../config/waypointIcons'

interface WalkActivity {
  id: number | string
  start_datetime: string
  end_datetime: string
  date: string
  strava_activity_id?: string
  distance_km: number
  elevation_gain_m: number
  elevation_loss_m?: number
  duration_hours: number
  activity_count?: number
  start_point: {
    type: 'Point'
    coordinates: [number, number]
    name: string
  }
  end_point: {
    type: 'Point'
    coordinates: [number, number]
    name: string
  }
  track: {
    type: 'LineString' | 'MultiLineString'
    coordinates: Array<[number, number, number]> | Array<Array<[number, number, number]>>
  }
  stats?: {
    max_elevation_m: number
    min_elevation_m: number
    avg_speed_kmh: number
    max_speed_kmh: number
  }
}

interface Waypoint {
  lat: number
  lng: number
  name?: string
  type?: 'checkpoint' | 'campsite' | 'endpoint' | 'water' | 'viewpoint' | 'other'
}

interface DaysListPanelProps {
  isOpen: boolean
  onClose: () => void
  activities: WalkActivity[]
  onDayClick: (dayIndex: number) => void
  getColorForDay: (index: number) => string
  waypoints?: Waypoint[]
}

const DaysListPanel = ({ isOpen, onClose, activities, onDayClick, getColorForDay, waypoints }: DaysListPanelProps) => {
  // Track if panel has been opened for proper animation
  const [hasBeenOpened, setHasBeenOpened] = React.useState(false)
  
  React.useEffect(() => {
    if (isOpen) setHasBeenOpened(true)
  }, [isOpen])
  
  if (activities.length === 0 || !hasBeenOpened) return null

  return (
    <>
      {/* Panel with strong shadow instead of backdrop */}
      <div 
        className={`fixed top-0 right-0 bottom-0 w-96 max-w-full bg-white z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0 shadow-[-10px_0_40px_rgba(0,0,0,0.3)]' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            Days Overview
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Waypoints Display */}
        {waypoints && waypoints.length > 0 && (
          <div className="p-4 bg-amber-50 border-b border-amber-200 flex-shrink-0">
            <div className="space-y-2">
              <div className="text-sm font-semibold text-amber-900">Waypoints</div>
              {waypoints.map((waypoint, index) => {
                const wpType = waypoint.type || 'checkpoint'
                const iconConfig = waypointIcons[wpType as keyof typeof waypointIcons] || waypointIcons.checkpoint
                const IconComponent = iconConfig.icon
                
                return (
                  <div key={index} className="flex items-center space-x-2">
                    <IconComponent className="w-4 h-4" style={{ color: iconConfig.color }} />
                    <div className="text-xs text-amber-700">
                      {waypoint.name || `${iconConfig.label} ${index + 1}`}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Days List - Now properly flexed to fill remaining space */}
        <div className="overflow-y-auto flex-1">
          <div className="p-4 space-y-4">
            {activities.map((activity, index) => (
              <div 
                key={activity.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onDayClick(index)}
              >
                {/* Day Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getColorForDay(index) }}
                    />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Day {index + 1}
                    </h3>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(activity.date).toLocaleDateString()}
                  </div>
                </div>

                {/* Key Stats */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">{activity.distance_km}km</div>
                    <div className="text-xs text-gray-500">Distance</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">↑{activity.elevation_gain_m} ↓{activity.elevation_loss_m ?? 0}m</div>
                    <div className="text-xs text-gray-500">Asc / Desc</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">{Math.round(activity.duration_hours * 10) / 10}h</div>
                    <div className="text-xs text-gray-500">Duration</div>
                  </div>
                </div>

                {/* Route Points */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: getColorForDay(index) }}
                    ></div>
                    <span className="text-gray-600 truncate">{activity.start_point.name}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <div
                      className="w-3 h-3 rounded-full border-2 bg-white flex-shrink-0"
                      style={{ borderColor: getColorForDay(index) }}
                    ></div>
                    <span className="text-gray-600 truncate">{activity.end_point.name}</span>
                  </div>
                </div>

                {/* Additional Stats */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Mountain size={12} />
                    <span>{activity.stats?.max_elevation_m || 0}m max</span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Clock size={12} />
                    <span>{Math.round((activity.stats?.avg_speed_kmh || (activity.distance_km / activity.duration_hours) || 0) * 10) / 10} km/h avg</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Footer */}
        <div className="bg-white border-t border-gray-200 p-4 flex-shrink-0">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-gray-900">
                {activities.reduce((sum, activity) => sum + activity.distance_km, 0).toFixed(1)}km
              </div>
              <div className="text-xs text-gray-500">Total Distance</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">
                ↑{activities.reduce((sum, activity) => sum + activity.elevation_gain_m, 0)} ↓{activities.reduce((sum, activity) => sum + (activity.elevation_loss_m ?? 0), 0)}m
              </div>
              <div className="text-xs text-gray-500">Ascent / Descent</div>
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">
                {activities.length}
              </div>
              <div className="text-xs text-gray-500">Days</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default DaysListPanel